import './App.css';

import { useEffect, useRef, useState } from 'react';
import GoogleAdsLanding from './GoogleAdsLanding';
import PressPage from './PressPage';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import {
  isPreviousInquiryBlockedError,
  resolveContactApiUrl,
  submitContactInquiry
} from './contactApi';
import { scrollToHash } from './scrollToHash';

import translations from './translations';

const LANGUAGE_STORAGE_KEY = 'genius-language';

const LANGUAGE_OPTIONS = [
  { code: 'es', label: 'Español', flag: `${process.env.PUBLIC_URL}/flags/es.svg` },
  { code: 'en', label: 'English', flag: `${process.env.PUBLIC_URL}/flags/en.svg` },
  { code: 'pt', label: 'Português (Brasil)', flag: `${process.env.PUBLIC_URL}/flags/br.svg` }
];

const CONTACT_API_URL = resolveContactApiUrl();
const GOOGLE_MAPS_URL = 'https://www.google.com/maps?cid=11923473788686163360';
const GOOGLE_MAPS_DIRECTIONS_URL = 'https://www.google.com/maps/dir/?api=1&destination=Genius+Software,+C%C3%B3rdoba+836,+Rosario,+Argentina';
const GOOGLE_MAPS_EMBED_URL = 'https://www.google.com/maps?q=Genius+Software,+C%C3%B3rdoba+836,+Rosario,+Argentina&z=16&output=embed';
const SITE_HOME_PATH = '/';
const GOOGLE_ADS_LANDING_PATH = '/googleads-landing-publicidadarg';
const PRESS_PATH = '/prensa';
const PRIVACY_POLICY_PATH = '/politica-de-privacidad';
const APP_HASH_SCROLL_OPTIONS = {
  headerSelector: '.top',
  extraOffset: 20
};

const CONTACT_FORM_FEEDBACK = {
  es: {
    sending: 'Enviando...',
    success: 'Consulta enviada. Te contactaremos pronto.',
    error: 'No se pudo enviar la consulta. Inténtalo nuevamente.',
    blocked: 'La consulta fue bloqueada porque ya enviaron una previamente desde esta conexión.'
  },
  en: {
    sending: 'Sending...',
    success: 'Inquiry sent. We will contact you soon.',
    error: 'Could not send the inquiry. Please try again.',
    blocked: 'This inquiry was blocked because one was already sent previously from this connection.'
  },
  pt: {
    sending: 'Enviando...',
    success: 'Consulta enviada. Entraremos em contato em breve.',
    error: 'Não foi possível enviar a consulta. Tente novamente.',
    blocked: 'A consulta foi bloqueada porque já havia um envio anterior desta conexão.'
  }
};

const icons = {
  software: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="4" width="17" height="15" rx="3" />
      <path d="M3.5 8h17" />
      <path d="M9.2 12.2l-2 2 2 2" />
      <path d="M14.8 12.2l2 2-2 2" />
      <path d="M11.6 12.3v3.8" />
    </svg>
  ),
  infra: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="4.5" width="16" height="6" rx="2" />
      <rect x="4" y="13.5" width="16" height="6" rx="2" />
      <circle cx="8" cy="7.5" r="1.1" />
      <circle cx="8" cy="16.5" r="1.1" />
      <path d="M12 7.5h6" />
      <path d="M12 16.5h6" />
      <path d="M6 11h12" />
    </svg>
  ),
  automation: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2.6" />
      <path d="M12 18.4V21" />
      <path d="M5 12h2.6" />
      <path d="M16.4 12H19" />
      <path d="M6.2 6.2l1.9 1.9" />
      <path d="M15.9 15.9l1.9 1.9" />
      <path d="M6.2 17.8l1.9-1.9" />
      <path d="M15.9 8.1l1.9-1.9" />
      <path d="M12.6 9.2l-1.8 3.6h2.4L11.4 16" />
    </svg>
  ),
  cloud: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 18h9a4 4 0 0 0 0-8 5.5 5.5 0 0 0-10.7 1.6A3.5 3.5 0 0 0 7 18z" />
      <path d="M12 12v6" />
      <path d="M9.5 15.5L12 18l2.5-2.5" />
    </svg>
  )
};

const processIcons = {
  discover: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="5.5" />
      <path d="M21 21l-4.2-4.2" />
      <path d="M11 8.5v5" />
      <path d="M8.5 11h5" />
    </svg>
  ),
  design: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M4 9h16" />
      <path d="M9 4v16" />
      <path d="M13 13h7" />
    </svg>
  ),
  build: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <path d="M7 10h4" />
      <path d="M7 14h2" />
      <path d="M13 14h4" />
    </svg>
  ),
  evolve: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 12a8 8 0 0 1 13.5-5.5" />
      <path d="M18 4v5h-5" />
      <path d="M20 12a8 8 0 0 1-13.5 5.5" />
      <path d="M6 20v-5h5" />
    </svg>
  )
};

const uiIcons = {
  coffee: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2 8h16v7a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
      <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
      <path d="M6 2v3" />
      <path d="M10 2v3" />
      <path d="M14 2v3" />
    </svg>
  )
};

const socialIcons = {
  instagram: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7.25 2h9.5A5.25 5.25 0 0 1 22 7.25v9.5A5.25 5.25 0 0 1 16.75 22h-9.5A5.25 5.25 0 0 1 2 16.75v-9.5A5.25 5.25 0 0 1 7.25 2zm0 1.8A3.45 3.45 0 0 0 3.8 7.25v9.5a3.45 3.45 0 0 0 3.45 3.45h9.5a3.45 3.45 0 0 0 3.45-3.45v-9.5a3.45 3.45 0 0 0-3.45-3.45zm9.9 1.55a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2zM12 6.85A5.15 5.15 0 1 1 6.85 12 5.15 5.15 0 0 1 12 6.85zm0 1.8A3.35 3.35 0 1 0 15.35 12 3.35 3.35 0 0 0 12 8.65z" />
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13.35 21v-7.65h2.55l.38-2.98h-2.93V8.47c0-.86.24-1.44 1.47-1.44h1.57V4.37c-.76-.08-1.52-.12-2.28-.11-2.26 0-3.82 1.38-3.82 3.91v2.2H7.72v2.98h2.57V21z" />
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5.5 6.45a1.45 1.45 0 1 1 0-2.9 1.45 1.45 0 0 1 0 2.9z" />
      <path d="M4.2 8.3h2.6V20H4.2z" />
      <path d="M8.9 8.3h2.5v1.6h.04c.35-.66 1.22-1.8 2.98-1.8 3.18 0 3.77 2.1 3.77 4.82V20h-2.6v-6.26c0-1.49-.03-3.4-2.07-3.4-2.08 0-2.4 1.63-2.4 3.3V20H8.9z" />
      <path d="M3 2.8h18A1.2 1.2 0 0 1 22.2 4v16A1.2 1.2 0 0 1 21 21.2H3A1.2 1.2 0 0 1 1.8 20V4A1.2 1.2 0 0 1 3 2.8zm0 1.8v14.8h18V4.6z" />
    </svg>
  )
};

const footerSocialLinks = [
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/genius.sistemas',
    icon: socialIcons.instagram
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/people/Genius-Software/61578637530295/',
    icon: socialIcons.facebook
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/genius-software-arg/',
    icon: socialIcons.linkedin
  }
];

const industryIcons = {
  manufacturing: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2 20h20" />
      <path d="M4 20V9l5 3V9l5 3V9l6 3v8" />
      <path d="M7 17h2" />
      <path d="M11 17h2" />
      <path d="M15 17h2" />
      <path d="M18 7V4" />
    </svg>
  ),
  agro: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20v-8" />
      <path d="M12 12c0-3-2-5-5-5 0 3 2 5 5 5z" />
      <path d="M12 12c0-3 2-5 5-5 0 3-2 5-5 5z" />
      <path d="M8 20h8" />
    </svg>
  ),
  logistics: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="10" width="11" height="6" rx="1.5" />
      <path d="M14 12h4l3 3v1h-7z" />
      <circle cx="7" cy="18" r="1.6" />
      <circle cx="17" cy="18" r="1.6" />
      <path d="M3 16h11" />
    </svg>
  ),
  energy: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13 2L6 13h5l-1 9 7-11h-5l1-9z" />
      <path d="M4 22h16" />
    </svg>
  ),
  health: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20s-7-4.5-7-9a4 4 0 0 1 7-2 4 4 0 0 1 7 2c0 4.5-7 9-7 9z" />
      <path d="M12 9v4" />
      <path d="M10 11h4" />
    </svg>
  ),
  services: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 12a8 8 0 0 1 16 0" />
      <rect x="2.5" y="12" width="4.5" height="6" rx="2.2" />
      <rect x="17" y="12" width="4.5" height="6" rx="2.2" />
      <path d="M7 18c1.5 1.5 3.5 2 5 2s3.5-.5 5-2" />
    </svg>
  )
};

const placeholderCircle = `${process.env.PUBLIC_URL}/placeholder-circle.svg`;
const heroPrimaryImages = [
  `${process.env.PUBLIC_URL}/tecnoinicio.png`,
  `${process.env.PUBLIC_URL}/software.png`,
  `${process.env.PUBLIC_URL}/redesinicio.png`,
  `${process.env.PUBLIC_URL}/app.png`,
  `${process.env.PUBLIC_URL}/wifi.png`
];
const splitPrimaryImages = [`${process.env.PUBLIC_URL}/equipogenius2025.png`];
const industriesPrimaryVideosLeft = [`${process.env.PUBLIC_URL}/industria1.mp4`];
const industriesPrimaryVideosRight = [`${process.env.PUBLIC_URL}/industria2.mp4`];

const CircleCluster = ({
  className = '',
  primaryAlt = 'Imagen principal',
  secondaryAlt = 'Imagen secundaria',
  showOrbit = false,
  primaryImages = [placeholderCircle],
  primaryVideos = [],
  secondaryImage = "/logogenius1.png",
  intervalMs = 4500
}) => {
  const images = primaryImages.length ? primaryImages : [placeholderCircle];
  const mediaItems = primaryVideos.length
    ? primaryVideos.map((src) => ({ type: 'video', src }))
    : images.map((src) => ({ type: 'image', src }));
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex >= mediaItems.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, mediaItems.length]);

  useEffect(() => {
    if (mediaItems.length < 2 || typeof window === 'undefined') {
      return undefined;
    }

    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;

    if (prefersReducedMotion) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % mediaItems.length);
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [intervalMs, mediaItems.length]);

  return (
    <div className={className ? `circle-cluster ${className}` : 'circle-cluster'}>
      <div className="circle-frame circle-frame--lg">
        <div className="circle-carousel">
          {mediaItems.map((item, index) => {
            const isActive = index === activeIndex;

            if (item.type === 'video') {
              return (
                <video
                  key={`${item.src}-${index}`}
                  className={`circle-carousel__video${isActive ? ' is-active' : ''}`}
                  src={item.src}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  aria-hidden={!isActive}
                />
              );
            }

            return (
              <img
                key={`${item.src}-${index}`}
                className={`circle-carousel__image${isActive ? ' is-active' : ''}`}
                src={item.src}
                alt={isActive ? primaryAlt : ''}
                aria-hidden={!isActive}
              />
            );
          })}
        </div>
      </div>
      <div className="circle-frame circle-frame--sm">
        <img src={secondaryImage} alt={secondaryAlt} />
      </div>
      {showOrbit && (
        <span className="circle-orbit" aria-hidden="true">
          <span className="circle-orbit__dot" />
        </span>
      )}
    </div>
  );
};

const ContactForm = ({
  content,
  contactFeedback,
  contactStatus,
  isSubmitting,
  onSubmit,
  className = '',
  style,
  nameInputRef,
  assistantIntro
}) => (
  <form
    className={['contact-form', className].filter(Boolean).join(' ')}
    style={style}
    onSubmit={onSubmit}
  >
    <div className="contact-form-header">
      {assistantIntro ? (
        <div className="contact-form-assistant">
          <span className="contact-form-assistant__icon" aria-hidden="true">
            {"\uD83D\uDC4B"}
          </span>
          <div className="contact-form-assistant__copy">
            <h3>{assistantIntro.title}</h3>
            <p>{assistantIntro.subtitle}</p>
          </div>
        </div>
      ) : (
        <>
          <h3>{content.contact.form.title}</h3>
          <p>{content.contact.form.subtitle}</p>
        </>
      )}
    </div>
    <div className="contact-form-grid">
      <label className="field">
        <span>{content.contact.form.nameLabel}</span>
        <input
          ref={nameInputRef}
          type="text"
          name="name"
          placeholder={content.contact.form.namePlaceholder}
          autoComplete="name"
          required
        />
      </label>
      <label className="field">
        <span>{content.contact.form.phoneLabel}</span>
        <input
          type="tel"
          name="phone"
          placeholder={content.contact.form.phonePlaceholder}
          autoComplete="tel"
          required
        />
      </label>
      <label className="field field-full">
        <span>{content.contact.form.emailLabel}</span>
        <input
          type="email"
          name="email"
          placeholder={content.contact.form.emailPlaceholder}
          autoComplete="email"
          required
        />
      </label>
      <label className="field field-full">
        <span>{content.contact.form.messageLabel}</span>
        <textarea
          name="message"
          rows="4"
          placeholder={content.contact.form.messagePlaceholder}
          required
        />
      </label>
    </div>
    {contactStatus.message ? (
      <p
        className={`contact-form-status ${
          contactStatus.type === 'success' ? 'is-success' : 'is-error'
        }`}
        role={contactStatus.type === 'error' ? 'alert' : 'status'}
      >
        {contactStatus.message}
      </p>
    ) : null}
    <button className="btn btn-primary" type="submit" disabled={isSubmitting}>
      {isSubmitting ? contactFeedback.sending : content.contact.form.submit}
    </button>
  </form>
);

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactStatus, setContactStatus] = useState({ type: 'idle', message: '' });
  const hasWarned = useRef(false);
  const [floatingContactOpen, setFloatingContactOpen] = useState(false);
  const floatingContactNameInputRef = useRef(null);
  const shouldFocusFloatingContactRef = useRef(false);
  const [languageModalOpen, setLanguageModalOpen] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);

    return !translations[storedLanguage];
  });
  const [language, setLanguage] = useState(() => {
    if (typeof window === 'undefined') {
      return 'es';
    }

    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);

    return translations[storedLanguage] ? storedLanguage : 'es';
  });
  const handleNavClick = () => {
    setMenuOpen(false);
    setLanguageMenuOpen(false);
  };

  const handleSectionLinkClick = (event) => {
    const href = event.currentTarget.getAttribute('href');

    if (!href?.startsWith('#')) {
      return;
    }

    event.preventDefault();
    handleNavClick();

    if (typeof window === 'undefined') {
      return;
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollToHash(href, APP_HASH_SCROLL_OPTIONS);
      });
    });
  };

  const handleWhatsAppConversion = () => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      return;
    }

    window.gtag('event', 'conversion', { send_to: 'AW-831277997/vVopCITt-vkaEK2XsYwD' });
  };

  const toggleFloatingContact = () => {
    setFloatingContactOpen((current) => {
      const nextOpen = !current;
      shouldFocusFloatingContactRef.current = nextOpen;
      return nextOpen;
    });
  };

  const closeFloatingContact = () => {
    shouldFocusFloatingContactRef.current = false;
    setFloatingContactOpen(false);
  };

  useEffect(() => {
    if (hasWarned.current) {
      return;
    }

    hasWarned.current = true;

    if (typeof window !== 'undefined' && window.console) {
      console.warn(
        'ATENCION: ESTA INDAGANDO UN CODIGO REGISTRADO EN EL REGISTRO DE LA PROPIEDAD INTELECTUAL DE ARGENTINA. SITIO WEB CREADO POR LUCHOR@'
      );
    }
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    if (typeof document === 'undefined' || !languageModalOpen) {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [languageModalOpen]);

  useEffect(() => {
    if (!floatingContactOpen || typeof window === 'undefined') {
      return undefined;
    }

    let focusTimer;

    if (shouldFocusFloatingContactRef.current) {
      focusTimer = window.setTimeout(() => {
        floatingContactNameInputRef.current?.focus();
        shouldFocusFloatingContactRef.current = false;
      }, 140);
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        shouldFocusFloatingContactRef.current = false;
        setFloatingContactOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      if (focusTimer) {
        window.clearTimeout(focusTimer);
      }
      window.removeEventListener('keydown', handleEscape);
    };
  }, [floatingContactOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    const revealElements = Array.from(document.querySelectorAll('.reveal'));

    if (!revealElements.length) {
      return;
    }

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      revealElements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, observerInstance) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observerInstance.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    revealElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [language]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const scrollFromCurrentHash = () => {
      if (!window.location.hash) {
        return;
      }

      window.requestAnimationFrame(() => {
        scrollToHash(window.location.hash, {
          ...APP_HASH_SCROLL_OPTIONS,
          updateHash: false
        });
      });
    };

    const initialTimer = window.setTimeout(scrollFromCurrentHash, 0);
    window.addEventListener('hashchange', scrollFromCurrentHash);

    return () => {
      window.clearTimeout(initialTimer);
      window.removeEventListener('hashchange', scrollFromCurrentHash);
    };
  }, []);

  const handleLanguageSelect = (code) => {
    if (!translations[code]) {
      return;
    }

    setLanguage(code);
    setLanguageMenuOpen(false);
    setLanguageModalOpen(false);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    }
  };

  const content = translations[language] ?? translations.es;
  const processSteps = content.process;
  const activeLanguage =
    LANGUAGE_OPTIONS.find((option) => option.code === language) ?? LANGUAGE_OPTIONS[0];
  const stackItems = content.stack?.items ?? [];
  const contactFeedback = CONTACT_FORM_FEEDBACK[language] ?? CONTACT_FORM_FEEDBACK.es;
  const floatingContactPanelId = 'floating-contact-panel';
  const currentPath =
    typeof window === 'undefined' ? '' : window.location.pathname.toLowerCase();
  const isGoogleAdsLanding =
    currentPath === GOOGLE_ADS_LANDING_PATH ||
    currentPath === `${GOOGLE_ADS_LANDING_PATH}/`;
  const isPressPage = currentPath === PRESS_PATH || currentPath === `${PRESS_PATH}/`;
  const isPrivacyPolicyPage =
    currentPath === PRIVACY_POLICY_PATH || currentPath === `${PRIVACY_POLICY_PATH}/`;
  const isStandalonePage = isPressPage || isPrivacyPolicyPage;

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.title = isPressPage
      ? `${content.footer.brand} | ${content.nav.press}`
      : isPrivacyPolicyPage
        ? `${content.footer.brand} | Política de Privacidad`
        : content.footer.brand;
  }, [content.footer.brand, content.nav.press, isPressPage, isPrivacyPolicyPage]);

  if (isGoogleAdsLanding) {
    return (
      <GoogleAdsLanding
        contactApiUrl={CONTACT_API_URL}
        onWhatsAppClick={handleWhatsAppConversion}
      />
    );
  }

  const brandHref = isStandalonePage ? SITE_HOME_PATH : '#inicio';
  const servicesHref = isStandalonePage ? `${SITE_HOME_PATH}#servicios` : '#servicios';
  const solutionsHref = isStandalonePage ? `${SITE_HOME_PATH}#soluciones` : '#soluciones';
  const stackHref = isStandalonePage ? `${SITE_HOME_PATH}#stack` : '#stack';
  const processHref = isStandalonePage ? `${SITE_HOME_PATH}#proceso` : '#proceso';
  const teamHref = isStandalonePage ? `${SITE_HOME_PATH}#nuestro-equipo` : '#nuestro-equipo';
  const contactHref = isStandalonePage ? `${SITE_HOME_PATH}#contacto` : '#contacto';
  const brandClickHandler = isStandalonePage ? handleNavClick : handleSectionLinkClick;
  const sectionClickHandler = isStandalonePage ? handleNavClick : handleSectionLinkClick;
  const navLinks = isStandalonePage
    ? [
        { href: SITE_HOME_PATH, label: content.nav.home, onClick: handleNavClick },
        { href: servicesHref, label: content.nav.services, onClick: handleNavClick },
        { href: teamHref, label: content.nav.team, onClick: handleNavClick },
        {
          href: PRESS_PATH,
          label: content.nav.press,
          onClick: handleNavClick,
          isCurrent: isPressPage
        },
        { href: contactHref, label: content.nav.contact, onClick: handleNavClick }
      ]
    : [
        { href: servicesHref, label: content.nav.services, onClick: sectionClickHandler },
        { href: solutionsHref, label: content.nav.solutions, onClick: sectionClickHandler },
        { href: stackHref, label: content.nav.stack, onClick: sectionClickHandler },
        { href: processHref, label: content.nav.process, onClick: sectionClickHandler },
        { href: teamHref, label: content.nav.team, onClick: sectionClickHandler },
        { href: PRESS_PATH, label: content.nav.press, onClick: handleNavClick },
        { href: contactHref, label: content.nav.contact, onClick: sectionClickHandler }
      ];

  const handleContactSubmit = async (event) => {
    event.preventDefault();

    const formElement = event.currentTarget;

    if (!formElement.checkValidity()) {
      formElement.reportValidity();
      return;
    }

    const formData = new FormData(formElement);
    const name = String(formData.get('name') ?? '').trim();
    const phone = String(formData.get('phone') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();
    const payload = {
      nombre: name,
      telefono: phone,
      email,
      consulta: message
    };

    setIsSubmitting(true);
    setContactStatus({ type: 'idle', message: '' });

    try {
      await submitContactInquiry(CONTACT_API_URL, payload);
      formElement.reset();
      setContactStatus({ type: 'success', message: contactFeedback.success });
    } catch (error) {
      console.error('No se pudo enviar la consulta:', error);
      setContactStatus({
        type: 'error',
        message: isPreviousInquiryBlockedError(error)
          ? contactFeedback.blocked
          : contactFeedback.error
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="site">
      <div className="ambient" aria-hidden="true">
        <span className="orb orb-one" />
        <span className="orb orb-two" />
        <span className="orb orb-three" />
        <span className="grid-lines" />
      </div>

      {languageModalOpen ? (
        <div
          className="language-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="language-modal-title"
        >
          <div className="language-modal__backdrop" aria-hidden="true" />
          <div className="language-modal__panel">
            <div className="language-modal__header">
              <p className="language-modal__helper">{content.modal.eyebrow}</p>
              <h2 id="language-modal-title">{content.modal.title}</h2>
              <p className="language-modal__subtitle">{content.modal.subtitle}</p>
            </div>
            <div className="language-modal__options">
              {LANGUAGE_OPTIONS.map((option) => {
                const isActive = option.code === language;

                return (
                  <button
                    key={option.code}
                    type="button"
                    className="language-option"
                    onClick={() => handleLanguageSelect(option.code)}
                    autoFocus={isActive}
                  >
                    <span className="language-option__flag" aria-hidden="true">
                      <img src={option.flag} alt="" />
                    </span>
                    <span className="language-option__name">{option.label}</span>
                    <span className="language-option__code">{option.code.toUpperCase()}</span>
                  </button>
                );
              })}
            </div>
            <p className="language-modal__helper">{content.modal.helper}</p>
          </div>
        </div>
      ) : null}

      <header className="top">
        <nav className={`nav ${menuOpen ? 'is-open' : ''}`}>
          <div className="nav-bar">
            <a className="brand" href={brandHref} onClick={brandClickHandler}>
              <span className="brand-mark" aria-hidden="true">
                <img src={`${process.env.PUBLIC_URL}/logox.png`} alt="" />
              </span>
              <span className="brand-text">
                <span>Genius</span>
                <span>Software</span>
              </span>
            </a>
            <button
              className="menu-toggle"
              type="button"
              aria-expanded={menuOpen}
              aria-controls="main-navigation"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="menu-icon" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
              <span className="menu-label">{content.nav.menu}</span>
            </button>
          </div>
          <div className="nav-panel" id="main-navigation">
            <div className="nav-links">
              {navLinks.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={item.onClick}
                  className={item.isCurrent ? 'is-current' : undefined}
                  aria-current={item.isCurrent ? 'page' : undefined}
                >
                  {item.label}
                </a>
              ))}
            </div>
            <div className="nav-actions">
              <div className="nav-language" role="group" aria-label={content.nav.language}>
                <span className="nav-language__label">{content.nav.language}</span>
                <div className={`nav-language__dropdown${languageMenuOpen ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className="nav-language__trigger"
                    aria-haspopup="menu"
                    aria-expanded={languageMenuOpen}
                    onClick={() => setLanguageMenuOpen((open) => !open)}
                  >
                    <span className="nav-language__flag" aria-hidden="true">
                      <img src={activeLanguage.flag} alt="" />
                    </span>
                    <span className="nav-language__code">
                      {activeLanguage.code.toUpperCase()}
                    </span>
                    <span className="nav-language__chevron" aria-hidden="true" />
                  </button>
                  <div className="nav-language__panel" role="menu">
                    {LANGUAGE_OPTIONS.map((option) => {
                      const isActive = option.code === language;

                      return (
                        <button
                          key={option.code}
                          type="button"
                          className={`nav-language__option${isActive ? ' is-active' : ''}`}
                          role="menuitemradio"
                          aria-checked={isActive}
                          onClick={() => handleLanguageSelect(option.code)}
                        >
                          <span className="nav-language__flag" aria-hidden="true">
                            <img src={option.flag} alt="" />
                          </span>
                          <span className="nav-language__code">
                            {option.code.toUpperCase()}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <a
                className="btn btn-primary nav-cta"
                href={contactHref}
                onClick={sectionClickHandler}
              >
                <span className="btn-icon" aria-hidden="true">
                  {uiIcons.coffee}
                </span>
                {content.nav.cta}
              </a>
            </div>
          </div>
        </nav>
      </header>

      {isPressPage ? (
        <PressPage content={content} />
      ) : isPrivacyPolicyPage ? (
        <PrivacyPolicyPage />
      ) : (
        <main>
        <section id="inicio" className="hero">
          <div className="container hero-grid">
            <div className="hero-copy reveal" style={{ '--delay': '0.05s' }}>
              <div className="badge">{content.hero.badge}</div>
              <h1 className="hero-title">{content.hero.title}</h1>
              <p className="hero-text">{content.hero.description}</p>
              <div className="hero-actions">
                <a className="btn btn-primary" href="#contacto" onClick={handleSectionLinkClick}>
                  {content.hero.actions.primary}
                </a>
                <a
                  className="btn btn-ghost"
                  href="https://wa.me/5493416926584"
                  target="_blank"
                  rel="noreferrer"
                  onClick={handleWhatsAppConversion}
                >
                  {content.hero.actions.whatsapp}
                </a>
                <a className="btn btn-ghost" href="#servicios" onClick={handleSectionLinkClick}>
                  {content.hero.actions.secondary}
                </a>
              </div>
              <div className="hero-highlights">
                {content.hero.highlights.map((highlight) => (
                  <div className="highlight" key={highlight.title}>
                    <span className="highlight-title">{highlight.title}</span>
                    <span className="highlight-text">{highlight.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="hero-visual reveal" style={{ '--delay': '0.15s' }}>
              <div className="orbital">
                <span className="ring ring-one" />
                <span className="ring ring-two" />
                <span className="ring ring-three" />
                <span className="node node-one" />
                <span className="node node-two" />
                <span className="node node-three" />
              </div>
              <CircleCluster
                className="circle-cluster--hero"
                primaryAlt={content.images.heroPrimaryAlt}
                secondaryAlt={content.images.heroSecondaryAlt}
                primaryImages={heroPrimaryImages}
                showOrbit
              />
            </div>
          </div>
        </section>

        <section id="servicios" className="section section-services">
          <div className="container">
            <div className="section-header reveal">
              <p className="eyebrow">{content.sections.services.eyebrow}</p>
              <h2 className="section-title">{content.sections.services.title}</h2>
              <p className="section-subtitle">{content.sections.services.subtitle}</p>
            </div>

            <div className="services-grid">
              {content.services.map((service, index) => (
                <article
                  className="service-card reveal"
                  style={{ '--delay': `${index * 0.1}s` }}
                  key={service.title}
                >
                  <div className="icon">{icons[service.icon]}</div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul>
                    {service.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="soluciones" className="section section-accent">
          <div className="container split">
            <div className="split-copy">
              <p className="eyebrow reveal" style={{ '--delay': '0.05s' }}>
                {content.sections.company.eyebrow}
              </p>
              <h2 className="section-title reveal" style={{ '--delay': '0.12s' }}>
                {content.sections.company.title}
              </h2>
              <p className="section-subtitle reveal" style={{ '--delay': '0.2s' }}>
                {content.sections.company.subtitle}
              </p>
              <div className="feature-list">
                {content.differentiators.map((item, index) => (
                  <div
                    className="feature-card reveal"
                    style={{ '--delay': `${index * 0.12}s` }}
                    key={item.title}
                  >
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="split-panel">
              <CircleCluster
                className="circle-cluster--split"
                primaryAlt={content.images.splitPrimaryAlt}
                secondaryAlt={content.images.splitSecondaryAlt}
                primaryImages={splitPrimaryImages}
              />
              <div className="panel-card reveal" style={{ '--delay': '0.1s' }}>
                <h3>{content.companyCard.title}</h3>
                <p>{content.companyCard.description}</p>
                <ul className="checklist">
                  {content.companyCard.checklist.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="panel-strip">
                <div className="reveal" style={{ '--delay': '0.18s' }}>
                  <span className="stat-label">{content.companyStrip.locationLabel}</span>
                  <span className="stat-value">{content.companyStrip.locationValue}</span>
                </div>
                <div className="reveal" style={{ '--delay': '0.26s' }}>
                  <span className="stat-label">{content.companyStrip.contactLabel}</span>
                  <span className="stat-value">{content.companyStrip.contactValue}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="proceso" className="section">
          <div className="container">
            <div className="section-header reveal">
              <p className="eyebrow">{content.sections.process.eyebrow}</p>
              <h2 className="section-title">{content.sections.process.title}</h2>
              <p className="section-subtitle">{content.sections.process.subtitle}</p>
            </div>

            <div className="process-grid">
              {processSteps.map((step, index) => (
                <div
                  className="process-step reveal"
                  style={{ '--delay': `${index * 0.1}s` }}
                  key={step.step}
                >
                  <div className="process-head">
                    <span className="process-icon">{processIcons[step.icon]}</span>
                    <span className="step-number">{step.step}</span>
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="stack" className="section">
          <div className="container stack-grid">
            <div className="stack-copy reveal">
              <p className="eyebrow">{content.sections.stack.eyebrow}</p>
              <h2 className="section-title">{content.sections.stack.title}</h2>
              <p className="section-subtitle">{content.sections.stack.subtitle}</p>
              <div className="stack-note">{content.stack.note}</div>
            </div>

            <div
              className="stack-list reveal"
              style={{ '--delay': '0.12s' }}
              aria-label={content.sections.stack.title}
            >
              {stackItems.map((item) => (
                <span className="chip" key={item}>
                  {item}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section id="industrias" className="section">
          <div className="container">
            <div className="section-header reveal">
              <p className="eyebrow">{content.sections.industries.eyebrow}</p>
              <h2 className="section-title">{content.sections.industries.title}</h2>
              <p className="section-subtitle">{content.sections.industries.subtitle}</p>
            </div>

            <div className="industry-visual">
              <CircleCluster
                className="circle-cluster--industries-left"
                primaryAlt={content.images.industriesPrimaryAlt}
                secondaryAlt={content.images.industriesSecondaryAlt}
                primaryVideos={industriesPrimaryVideosLeft}
              />
              <div className="industry-connector" aria-hidden="true">
                <span className="connector-dot dot-one" />
                <span className="connector-dot dot-two" />
                <span className="connector-dot dot-three" />
              </div>
              <CircleCluster
                className="circle-cluster--industries"
                primaryAlt={content.images.industriesPrimaryAlt}
                secondaryAlt={content.images.industriesSecondaryAlt}
                primaryVideos={industriesPrimaryVideosRight}
              />
            </div>

            <div className="industry-grid">
              {content.industries.map((item, index) => (
                <div
                  className="industry-card reveal"
                  style={{ '--delay': `${index * 0.08}s` }}
                  key={item.title}
                >
                  <span className="industry-icon" aria-hidden="true">
                    {industryIcons[item.icon]}
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="equipo" className="section" aria-labelledby="trust-heading">
          <div className="container trust-showcase reveal">
            <div className="trust-showcase__halo" aria-hidden="true">
              <span className="trust-showcase__orbit trust-showcase__orbit--outer" />
              <span className="trust-showcase__orbit trust-showcase__orbit--inner" />
              <span className="trust-showcase__spark trust-showcase__spark--one" />
              <span className="trust-showcase__spark trust-showcase__spark--two" />
              <span className="trust-showcase__spark trust-showcase__spark--three" />
              <span className="trust-showcase__metric">{content.sections.team.metric}</span>
            </div>

            <div className="trust-showcase__copy">
              <p className="eyebrow">{content.sections.team.eyebrow}</p>
              <h2
                className="trust-showcase__title"
                id="trust-heading"
                aria-label={`${content.sections.team.metric} ${content.sections.team.title}`}
              >
                {content.sections.team.title}
              </h2>
              <p className="trust-showcase__subtitle">{content.sections.team.subtitle}</p>
            </div>
          </div>
        </section>

        <section id="nuestro-equipo" className="section team-section">
          <div className="container">
            <div className="section-header team-section__header reveal">
              <p className="eyebrow">{content.sections.teamMembers.eyebrow}</p>
              <h2 className="section-title">{content.sections.teamMembers.title}</h2>
              <p className="section-subtitle">{content.sections.teamMembers.subtitle}</p>
            </div>

            <div className="team-grid">
              {content.team.map((member, index) => (
                <article
                  className="team-card reveal"
                  style={{ '--delay': `${index * 0.07}s` }}
                  key={member.name}
                >
                  <div className="avatar">
                    {member.photo ? (
                      <img
                        src={member.photo}
                        alt={member.name}
                        loading="lazy"
                        style={{
                          objectPosition: member.photoPosition,
                          transformOrigin: member.photoPosition,
                          transform: member.photoScale ? `scale(${member.photoScale})` : undefined
                        }}
                      />
                    ) : (
                      member.initials
                    )}
                  </div>
                  <div className="team-card__copy">
                    <h3>{member.name}</h3>
                    <p>{member.role}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="contacto" className="section contact">
          <div className="container contact-grid">
            <ContactForm
              content={content}
              contactFeedback={contactFeedback}
              contactStatus={contactStatus}
              isSubmitting={isSubmitting}
              onSubmit={handleContactSubmit}
              className="reveal"
              style={{ '--delay': '0.05s' }}
            />

            <div className="contact-panel">
              <div className="contact-copy reveal" style={{ '--delay': '0.14s' }}>
                <p className="eyebrow">{content.sections.contact.eyebrow}</p>
                <h2 className="section-title">{content.sections.contact.title}</h2>
                <p className="section-subtitle">{content.sections.contact.subtitle}</p>
                <div className="contact-actions">
                  <a className="btn btn-primary" href={`mailto:${content.contact.values.email}`}>
                    {content.contact.actions.email}
                  </a>
                  <a className="btn btn-ghost" href="tel:+543416926584">
                    {content.contact.actions.call}
                  </a>
                </div>
              </div>

              <div className="contact-card reveal" style={{ '--delay': '0.22s' }}>
                <div className="contact-row">
                  <span className="contact-label">{content.contact.card.webLabel}</span>
                  <a href="https://www.sistemasgenius.com">{content.contact.values.web}</a>
                </div>
                <div className="contact-row">
                  <span className="contact-label">{content.contact.card.emailLabel}</span>
                  <a href={`mailto:${content.contact.values.email}`}>
                    {content.contact.values.email}
                  </a>
                </div>
                <div className="contact-row">
                  <span className="contact-label">{content.contact.card.phoneLabel}</span>
                  <a href="tel:+543416926584">{content.contact.values.phone}</a>
                </div>
                <div className="contact-row">
                  <span className="contact-label">{content.contact.card.locationLabel}</span>
                  <a href={GOOGLE_MAPS_URL} target="_blank" rel="noreferrer">
                    {content.contact.values.location}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        </main>
      )}

      <footer className="footer">
        <div className="container footer-grid">
          <div>
            <div className="footer-brand">{content.footer.brand}</div>
            <p>{content.footer.description}</p>
            {content.footer.legalLines?.length ? (
              <div className="footer-fiscal">
                <h4>{content.footer.fiscalTitle}</h4>
                {content.footer.legalLines.map((item) => (
                  <p key={item}>{item}</p>
                ))}
              </div>
            ) : null}
          </div>
          <div>
            <h4>{content.footer.servicesTitle}</h4>
            <ul>
              {content.footer.servicesItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div>
            <h4>{content.footer.contactTitle}</h4>
            <a className="footer-address" href={GOOGLE_MAPS_URL} target="_blank" rel="noreferrer">
              {content.contact.values.location}
            </a>
            {content.footer.contactItems.map((item) => (
              <p key={item}>{item}</p>
            ))}
            <div className="footer-map">
              <iframe
                title={content.footer.mapTitle}
                src={GOOGLE_MAPS_EMBED_URL}
                width="600"
                height="200"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <div className="footer-map-links">
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noreferrer">
                {content.footer.mapsLabel}
              </a>
              <a href={GOOGLE_MAPS_DIRECTIONS_URL} target="_blank" rel="noreferrer">
                {content.footer.directionsLabel}
              </a>
            </div>
            <div className="footer-socials">
              {footerSocialLinks.map((item) => (
                <a
                  key={item.name}
                  className="footer-social-link"
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={item.name}
                  title={item.name}
                >
                  <span aria-hidden="true">{item.icon}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <div className="floating-contact">
        <div
          id={floatingContactPanelId}
          className={`contact-widget${floatingContactOpen ? ' is-open' : ''}`}
          role="dialog"
          aria-modal="false"
          aria-hidden={!floatingContactOpen}
        >
          <button
            type="button"
            className="contact-widget__close"
            aria-label={content.contact.widget.closeLabel}
            onClick={closeFloatingContact}
          >
            <span aria-hidden="true">×</span>
          </button>
          <ContactForm
            content={content}
            contactFeedback={contactFeedback}
            contactStatus={contactStatus}
            isSubmitting={isSubmitting}
            onSubmit={handleContactSubmit}
            className="contact-form--floating"
            nameInputRef={floatingContactNameInputRef}
            assistantIntro={content.contact.widget.assistant}
          />
        </div>

        <div className="floating-contact__actions">
          <button
            type="button"
            className={`contact-fab${floatingContactOpen ? ' is-active' : ''}`}
            aria-label={content.contact.widget.openLabel}
            aria-controls={floatingContactPanelId}
            aria-expanded={floatingContactOpen}
            onClick={toggleFloatingContact}
          >
            <span className="contact-fab__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M5 6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v6a2.5 2.5 0 0 1-2.5 2.5H12l-4.2 3.1c-.8.6-1.8 0-1.8-1V15A2.5 2.5 0 0 1 5 12.5z" />
                <path d="M8.5 8.8h7" />
                <path d="M8.5 11.8h4.8" />
              </svg>
            </span>
            <span className="contact-fab__text">{content.contact.widget.button}</span>
          </button>
        </div>
      </div>

      <div className="floating-whatsapp">
        <a
          className="whatsapp-fab"
          href="https://wa.me/5493416926584"
          target="_blank"
          rel="noreferrer"
          aria-label={content.whatsapp.label}
          onClick={handleWhatsAppConversion}
        >
          <span className="whatsapp-fab__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 4a8 8 0 0 0-6.9 12.1L4 20l3.9-1A8 8 0 1 0 12 4z" />
              <path d="M9.4 9.6c.2-.4.4-.4.6-.4h.5c.2 0 .4.1.5.3l.7 1.5c.1.2.1.4 0 .6l-.4.5a.4.4 0 0 0 0 .5c.4.7 1.1 1.4 1.9 1.9a.4.4 0 0 0 .5 0l.5-.4c.2-.1.4-.1.6 0l1.5.7c.2.1.3.3.3.5v.5c0 .2 0 .4-.2.6-.2.2-.8.6-1.8.6-1.2 0-2.7-.6-3.9-1.7-1.2-1.1-2-2.6-2-3.8 0-.9.3-1.5.5-1.7z" />
            </svg>
          </span>
          <span className="whatsapp-fab__text">{content.whatsapp.text}</span>
        </a>
      </div>
    </div>
  );
}

export default App;
